import classNames from "classnames/bind";
import React, { useMemo, useState } from "react";
import styles from "./Radio.module.scss";
import type { RadioOptionProps } from "./types";

const cx = classNames.bind(styles);

/**
 * Radio buttons allow a user to make a single selection out of multiple choices
 */
const RadioOption = ({
  name,
  index,
  option,
  getLabel,
  getValue,
  onChange,
  value,
  isDisabled,
  isLoading,
}: RadioOptionProps) => {
  const [isHovered, setIsHovered] = useState(false);

  const id = useMemo(() => `${name}-${index}`, [name, index]);
  const label = useMemo(() => getLabel(option), [option, getLabel]);
  const optionValue = useMemo(() => getValue(option), [option, getValue]);

  const isChecked = useMemo(() => {
    if (value === "true" || value === "false") {
      return optionValue.toString() === value;
    }
    return optionValue === value;
  }, [value, optionValue]);

  const onClick = () => onChange(optionValue);

  const onMouseEnter = () => setIsHovered(true);

  const onMouseLeave = () => setIsHovered(false);

  return (
    <div
      className={cx("radioOptionContainer", {
        skeleton: isLoading,
        isDisabled,
      })}
      onClick={onClick}
      onMouseEnter={onMouseEnter}
      onMouseLeave={onMouseLeave}
    >
      <input
        type="radio"
        name={name}
        id={id}
        className={cx("radioInput")}
        checked={isChecked}
        aria-labelledby={`${id}-label`}
        readOnly
      />

      {!isLoading ? (
        <div
          className={cx("radioIconContainer", {
            isChecked,
            isHovered,
            isDisabled,
          })}
        >
          <svg
            width="16"
            height="16"
            viewBox="0 0 16 16"
            fill="none"
            xmlns="http://www.w3.org/2000/svg"
            className={cx("radioIcon")}
          >
            {isChecked ? (
              <path
                d={"M3.4,6.8 L5.95,9.35 L10.2,4.25"}
                stroke="white"
                strokeWidth={"2"}
                strokeLinecap="round"
                strokeLinejoin="round"
              />
            ) : null}
          </svg>
        </div>
      ) : null}

      <label
        className={cx("radioOptionLabel")}
        htmlFor={name}
        id={`${id}-label`}
      >
        {label}
      </label>
    </div>
  );
};

export default RadioOption;
